import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import {
  Button,
} from '@mantine/core';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReverseMortgageCalculateMutation, useSaveMutation } from '@/services/api';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import { getMutationResult } from '@/helpers/query';
import { useTranslation } from 'react-i18next';
import { formatNumberWithCommas } from '@/helpers/string';
import {
  Page,
  ProcessHeader,
  Modal,
  AmountSlider,
} from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { toISOString } from '@/helpers/date';

import { useDisclosure } from '@mantine/hooks';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';

import { updateReverseMortgage } from '@/data/features/reverseMortgage/reverseMortgage';
import { notifications } from '@mantine/notifications';
import { removeNumberFormatting } from '@/helpers/number';
import ProcessButton from '@/components/ProcessButton';
import MoneyBagIcon from '@/assets/images/moneyBag.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './index.module.css';

const LoanAmountReverseMortgage = () => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const [loading, setLoading] = useState(false);
  const [apiConfigMortgageCalculat] = useReverseMortgageCalculateMutation();
  const dispatch = useDispatch();
  const [apiSave] = useSaveMutation();
  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);

  const required = t('globals.validations.required');
  const numbers = t('globals.validations.number');
  useIdInStoreGuard();

  const {
    highestLoan,
    propertyValuation,
    youngestBorrowerDOB,
    oldestBorrowerDOB,
  } = extraDetails;

  const formValidations = {
    loanAmount: (value) => {
      if (!value || typeof value !== 'string') return required;
      // Remove commas and check if it's a valid number
      const cleanValue = value.replace(/[₪,\s]/g, '');
      if (!/^\d+$/.test(cleanValue)) return numbers;
      return null;
    },
  };

  const initialValues = {
    loanAmount: highestLoan ? formatNumberWithCommas(highestLoan.toString()) : '300,000',
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: formValidations,
    validateInputOnSubmit: true,
  });

  const handleContinue = async () => {
    try {
      setLoading(true);

      // Validate form
      const validation = form.validate();
      if (validation.hasErrors) {
        return;
      }
      const { loanAmount } = form.values;

      // Keep the value as a string for display purposes
      const formData = {
        ...form.values,
        loanAmount: removeNumberFormatting(loanAmount), // Keep as number
      };

      // Save data to API
      const objectToSend = {
        ...formData,
        id,
      };

      const { isError } = await getMutationResult(objectToSend, apiSave);

      if (isError) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
        return;
      }

      const objectToSendForReverseMortgage = {
        youngestBorrower: toISOString(youngestBorrowerDOB || oldestBorrowerDOB),
        propertyValuation,
      };

      const {
        data: reverseMortgageData,
        isError: isErrorForReverseMortgage,
      } = await getMutationResult(
        objectToSendForReverseMortgage,
        apiConfigMortgageCalculat,
      );

      if (reverseMortgageData?.data.length === 0) {
        setLoading(false);
        open();
        return;
      }

      if (!isErrorForReverseMortgage) {
        dispatch(updateReverseMortgage(reverseMortgageData.data));
      } else {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      }

      const {
        data: reverseMortgageDataForOffer,
      } = await getMutationResult({
        youngestBorrower: toISOString(youngestBorrowerDOB || oldestBorrowerDOB),
        amount: removeNumberFormatting(loanAmount),
        reverseMortgageAction: extraDetails.reverseMortgageAction,
        propertyValuation,
      }, apiConfigMortgageCalculat);
      dispatch(updateReverseMortgage(reverseMortgageDataForOffer.data));

      // Save data to store
      dispatch(updateExtraDetails(formData));

      // Navigate to next step
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'reverseMortgage').path, { id }));
    } catch (error) {
      console.error('Error saving loan amount data:', error);
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'reverseMortgageRouteType').path, { id }));
  };

  return (
    <ProcessHeader
      headerText="גובה ההלוואה המבוקש"
      headerColor="#E8F5E8"
      headerTextColor="#2E7D32"
      subtitle="בחרו את גובה ההלוואה המבוקש על פי הנתונים שהוקלדו"
      showReturnButton
      onReturn={handleReturn}
      icon={MoneyBagIcon}
    >
      <form onSubmit={form.onSubmit(handleContinue)}>
        <Page.Section
        title="גובה ההלוואה המבוקשת בהתאם לנתונים שהוקלדו"
        withBackground
        >
          <div className={classes.contentContainer}>
            <AmountSlider
              value={form.values.loanAmount}
              onChange={(value) => form.setFieldValue('loanAmount', value)}
              min={200000}
              max={Number(highestLoan) || 300000}
              step={10000}

              size="xl"
              showValue={true}
              valueSuffix=" ש״ח"
            />
          </div>
        </Page.Section>
        <Page.Actions>
          <ProcessButton type="submit" loading={loading} />
        </Page.Actions>
      </form>
      <Modal
        opened={opened}
        close={close}
        title={t('pages.reason.reverseMortgage.modal.title')}
        footer={<Button onClick={close}>{t('globals.close')}</Button>}
        showFooterBackground
        size="lg"
      >
        <div>{t('pages.reason.reverseMortgage.modal.body')}</div>
      </Modal>
    </ProcessHeader>
  );
};

export default LoanAmountReverseMortgage;
